import React, { useEffect, useState } from "react";
import { MessageCircle, Repeat, Building2, ShieldAlert, ExternalLink, Activity } from "lucide-react";

/**
 * STEP 18 · Anti-Sanction P2P Withdrawal Guidance Sidebar.
 *
 * Interactive stylized panel injected next to any Withdrawal / Payout modal.
 * Provides three curated off-ramp routes (Telegram Wallet P2P, BestChange,
 * Bybit/BingX) so users can convert Polygon USDT into local fiat cards
 * (Sber, T-Bank, Kaspi, Halyk, Uzcard, etc.) bypassing central-bank blocks.
 *
 * Renders without disturbing the primary on-chain withdrawal flow.
 */
const CARDS = [
  {
    key: "telegram-wallet",
    title: "Вывод через Telegram Wallet (P2P)",
    subtitle: "Прямой обмен внутри мессенджера на карты любых банков (Сбер, Т-Банк, Kaspi, Halyk, Uzcard) без комиссий через P2P-маркет Telegram.",
    href: "https://t.me/wallet",
    icon: MessageCircle,
    color: "#00F2FF",
    tag: "P2P · 0% комиссия",
  },
  {
    key: "bestchange",
    title: "Вывод через Мониторинг BestChange",
    subtitle: "Мгновенный обмен USDT Polygon на национальную фиатную валюту любого государства через независимые сертифицированные обменные пункты.",
    href: "https://www.bestchange.com/usdt-to-cash.html",
    icon: Repeat,
    color: "#FFD700",
    tag: "150+ обменников",
  },
  {
    key: "bybit-bingx",
    title: "Вывод через Биржи Bybit / BingX",
    subtitle: "Депозит на независимые азиатские платформы с мгновенным выводом наличных на локальные карты в 1 клик.",
    href: "https://www.bybit.com/en/nft/p2p/deposit",
    href2: "https://bingx.com/en/p2p/",
    icon: Building2,
    color: "#BC13FE",
    tag: "Instant cash-out",
  },
];

export function AntiSanctionSidebar({ style = {} }) {
  // STEP 21 — Live P2P Rate Ticker: polls /support/p2p-rates every 30 s.
  const [ticker, setTicker] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const load = async () => {
      try {
        const r = await fetch(`${backend}/api/tgc/support/p2p-rates`);
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled) setTicker(j);
      } catch { /* offline / preview slug — keep last snapshot */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <aside data-testid="anti-sanction-sidebar"
      style={{
        borderRadius: 14, padding: 18,
        background: "linear-gradient(180deg, rgba(188,19,254,0.08) 0%, rgba(0,242,255,0.04) 60%, rgba(0,0,0,0.30) 100%)",
        border: "1px solid rgba(188,19,254,0.35)",
        boxShadow: "0 20px 60px rgba(188,19,254,0.15)",
        ...style,
      }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 14 }}>
        <div style={{ padding: 8, background: "rgba(188,19,254,0.15)", borderRadius: 10, border: "1px solid #BC13FE55" }}>
          <ShieldAlert size={20} color="#BC13FE" />
        </div>
        <div>
          <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#BC13FE", fontWeight: 800 }}>
            Anti-Sanction P2P Gateway
          </div>
          <h3 style={{ margin: "4px 0 0", fontSize: 15, color: "#E7ECFF", lineHeight: 1.3 }}>
            Инструкция по безопасному выводу на банковские карты (P2P / Анти-санкционный шлюз)
          </h3>
        </div>
      </div>

      {/* STEP 21 — Live P2P Rate Ticker (polls every 30 s) */}
      <div data-testid="p2p-rate-ticker"
        style={{
          marginBottom: 14, padding: 12, borderRadius: 12,
          background: "linear-gradient(135deg, rgba(16,244,167,0.06), rgba(0,242,255,0.04))",
          border: "1px solid rgba(16,244,167,0.30)",
        }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                      gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Activity size={14} color="#10F4A7" />
            <div style={{ fontSize: 11, fontWeight: 800, color: "#10F4A7",
                          letterSpacing: 1.4, textTransform: "uppercase" }}>
              Актуальные курсы обмена P2P
            </div>
          </div>
          <div style={{ fontSize: 9, color: "#8B94B8" }}>
            Данные BestChange / Binance P2P · обновление 30 с
          </div>
        </div>
        <div data-testid="p2p-rate-list"
          style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 8 }}>
          {(ticker?.rates || [
            { quote: "RUB", rate: "…" }, { quote: "KZT", rate: "…" }, { quote: "UZS", rate: "…" },
          ]).map((r) => (
            <div key={r.quote} data-testid={`p2p-rate-${r.quote.toLowerCase()}`}
              style={{ padding: "8px 10px", borderRadius: 8,
                       background: "rgba(0,0,0,0.30)", border: "1px solid rgba(16,244,167,0.20)" }}>
              <div style={{ fontSize: 9, color: "#8B94B8", letterSpacing: 1.2,
                            textTransform: "uppercase", marginBottom: 2 }}>
                USDT ➔ {r.quote}
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#10F4A7", lineHeight: 1.1 }}>
                {typeof r.rate === "number"
                  ? r.rate.toLocaleString("ru-RU", { maximumFractionDigits: 2 })
                  : r.rate}
              </div>
              {r.channel && (
                <div style={{ fontSize: 9, color: "#5F6884", marginTop: 3 }}>{r.channel}</div>
              )}
            </div>
          ))}
        </div>
        <div data-testid="p2p-liquidity-indicator"
          style={{ display: "flex", alignItems: "center", gap: 6,
                   padding: "6px 10px", borderRadius: 999,
                   background: "rgba(16,244,167,0.12)",
                   border: "1px solid rgba(16,244,167,0.55)", width: "fit-content" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%",
                         background: "#10F4A7",
                         boxShadow: "0 0 10px #10F4A7, 0 0 20px #10F4A733",
                         animation: "pulse-green 1.6s ease-in-out infinite" }} />
          <span style={{ fontSize: 10, fontWeight: 800, color: "#10F4A7",
                         letterSpacing: 1.2, textTransform: "uppercase" }}>
            {ticker?.liquidity_label || "● СЕТЬ ЛИКВИДНА / ВЫВОД БЕЗ ОГРАНИЧЕНИЙ"}
          </span>
        </div>
        <style>{`@keyframes pulse-green { 0%,100% { opacity: 1 } 50% { opacity: 0.45 } }`}</style>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {CARDS.map((c) => (
          <div key={c.key} role="link" tabIndex={0}
            onClick={() => window.open(c.href, "_blank", "noreferrer")}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") window.open(c.href, "_blank", "noreferrer"); }}
            data-testid={`anti-sanction-card-${c.key}`}
            style={{
              display: "block", padding: 14, borderRadius: 12, textDecoration: "none",
              background: `linear-gradient(135deg, ${c.color}12, rgba(255,255,255,0.02))`,
              border: `1px solid ${c.color}55`, transition: "all 0.18s ease",
              color: "#E7ECFF", cursor: "pointer",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 12px 30px ${c.color}30`; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)";  e.currentTarget.style.boxShadow = "none"; }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{ padding: 6, borderRadius: 8, background: `${c.color}22`, border: `1px solid ${c.color}55` }}>
                <c.icon size={16} color={c.color} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: c.color, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  {c.title}
                  <ExternalLink size={12} color={c.color} style={{ opacity: 0.7 }} />
                </div>
                <div style={{ fontSize: 10, color: "#8B94B8", marginTop: 2 }}>{c.tag}</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: "#8B94B8", lineHeight: 1.5, paddingLeft: 34 }}>{c.subtitle}</div>
            {c.href2 && (
              <div style={{ marginTop: 6, paddingLeft: 34 }}>
                <a href={c.href2} target="_blank" rel="noreferrer" data-testid={`anti-sanction-card-${c.key}-alt`}
                  onClick={(e) => e.stopPropagation()}
                  style={{ fontSize: 11, color: c.color, textDecoration: "underline" }}>
                  + альтернативная площадка →
                </a>
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 14, padding: 10, borderRadius: 8,
                    background: "rgba(16,244,167,0.05)", border: "1px solid rgba(16,244,167,0.30)",
                    fontSize: 11, color: "#8B94B8", lineHeight: 1.5 }}>
        <b style={{ color: "#10F4A7" }}>Важно:</b> ваш USDT остаётся в блокчейне Polygon (chain 137) до момента P2P-сделки. Ни одна из площадок выше не связана с TGC и не может задержать вывод. Turbo Global Coin не берёт комиссию с P2P-обмена.
      </div>
    </aside>
  );
}

export default AntiSanctionSidebar;
